/**
 * Auth Store
 *
 * Manages authentication state including:
 * - Current user info
 * - JWT token
 * - Login/logout/refresh operations
 *
 * Uses localStorage for token persistence across sessions.
 */
import { defineStore } from 'pinia'
import { ref, computed, watch, getCurrentScope, onScopeDispose } from 'vue'
import type { User, UserRole, LoginRequest, PortalIdentitySession } from '../types/auth'
import { authService, type LogoutReason } from '../services/auth.service'
import { isPortalIdentityMode } from '../services/auth-mode'
import { clearSignInRedirect, markSigningOut } from '../services/sign-in-redirect'
import { resetDemoOverlay } from '../services/demo/overlay'
// No-ops unless an app installed the PostHog plugin AND the host is on its
// allowlist, so this import is inert in every app that has not been switched on.
// It costs the bundle nothing either: the SDK itself is behind a dynamic import.
import { syncIdentity } from '../plugins/posthog'
// The route-denial carrier (AST3-1). A denial holds the full URL a bounce threw
// away — arbitrary query and hash, so potentially a client or driver identifier
// — and before this import it outlived the session that produced it and was
// readable by whoever signed in next on the tab. `../router/denial-state`
// imports `vue` and nothing else precisely so this store can hold it: the guard
// that writes denials imports THIS module, so the dependency only runs one way.
import { beginAuthSession, clearRouteDenial } from '../router/denial-state'

const TOKEN_KEY = 'claimshield_token'
const REFRESH_TOKEN_KEY = 'claimshield_refresh_token'
const VIEW_AS_KEY = 'claimshield_view_as'
const VIEW_AS_CLIENT_KEY = 'claimshield_view_as_client'

/**
 * Identity-reset registry (S22-C2 for the legal stores; atlas close-out D1 for
 * every other per-user store).
 *
 * A Pinia store is a singleton that outlives a logout and a login as someone
 * else inside the SPA. Every store that holds one signed-in user's data
 * registers a reset here from its own setup(), and the auth store runs all of
 * them on BOTH paths that change who is signed in: `clearAuth()` (logout, a
 * failed session restore) and a sync watcher on `user.id` (an account replaced
 * without a teardown — a login over a live session, the CRM `?t=` handoff).
 * Before this, each store had to know to install its own watcher; three did
 * not, and RV-FE showed the next user A's notifications, support requests and
 * search (N1–N3).
 *
 * WHY A REGISTRY AND NOT AN IMPORT. `@claimshield/shared` is a dependency of
 * `@claimshield/ops`, not the other way round — importing an ops store from
 * here would be a cycle. Inversion also keeps the failure honest: a store that
 * never registers is visibly absent from this set rather than silently
 * covered by a wildcard.
 *
 * A reset must be safe to call on an already-clean store: logout runs it from
 * `clearAuth()` and again from the watcher as `user` goes to null. A
 * registration made inside a store's setup() leaves the set when that store is
 * disposed (`onScopeDispose`), so a dead store is never reset.
 */
const identityResets = new Set<() => void>()

/** Register a per-user store's reset. Returns a de-registration fn. */
export function registerIdentityReset(reset: () => void): () => void {
  identityResets.add(reset)
  const off = () => void identityResets.delete(reset)
  if (getCurrentScope()) onScopeDispose(off)
  return off
}

/** Call every registered reset. One failing reset must not stop the rest. */
export function resetIdentityStores(): void {
  for (const reset of identityResets) {
    try {
      reset()
    } catch (e) {
      console.error('[Auth] A store reset threw on an identity change:', e)
    }
  }
}

/** The legal stores' names for the same registry (S22-C2) — one mechanism. */
export const registerLegalStoreReset = registerIdentityReset
export const resetRegisteredLegalStores = resetIdentityStores

/**
 * True when `error` is the server ANSWERING that the session is not valid
 * (401/403), false when the request never got a usable answer (no response,
 * timeout, 5xx). Only the first may end a sign-in.
 */
export function isServerRejection(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status
  return status === 401 || status === 403
}

export const useAuthStore = defineStore('auth', () => {
  // ===== State =====
  const mode: 'password' | 'oidc' = import.meta.env.VITE_STAFF_OIDC === '1' && !isPortalIdentityMode()
    ? 'oidc' : 'password'
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const portalSession = ref<PortalIdentitySession | null>(null)
  const staffSession = ref<{ person_id: string; csrf_token: string; expires_at: number } | null>(null)
  const staffViewAsContext = ref(false)
  const initialized = ref(false)
  /**
   * True when the last session restore could not REACH the server (network
   * error, timeout, 5xx) — as opposed to the server answering that the token is
   * no good. The stored sign-in is kept; the login page offers a retry instead
   * of a blank form. Until 2026-10-02 every restore failure cleared the
   * sign-in, so a Wi-Fi drop or a deploy restart logged staff out (ops audit V6).
   */
  const restoreUnreachable = ref(false)
  const loading = ref(false)

  // View As feature - allows admins to see the UI as another user
  const viewingAs = ref<User | null>(null)

  // Portal staff "view-as client": a STAFF/ADMIN browsing the client portal can
  // pick a client to scope every tab to (read-only). Distinct from `viewingAs`
  // (which swaps the effective USER for the standalone /view-as token tab) — here
  // the user stays staff (so the picker stays available) and only the data scope
  // + sidebar name change. Persisted to sessionStorage so it survives navigation.
  const viewAsClientId = ref<string | null>(null)
  const viewAsClientName = ref<string | null>(null)
  // Edit-on-behalf: staff/admin explicitly toggled "edit as this client". NOT
  // persisted — opt-in per session (default off every reload). Gates the
  // X-Edit-On-Behalf header the api interceptor sends, which the backend
  // (resolve_client_for_write) requires before letting staff write to the
  // viewed client. Spec: knowledge/portal/specs/2026-06-22-edit-on-behalf-design.md
  const editOnBehalf = ref(false)

  // Demo mode: staff/admin explicitly toggled "demo on this client". Registered
  // portal mutations are intercepted client-side (never sent to the server);
  // everything else stays read-only via the server 403 backstop. Mutually
  // exclusive with editOnBehalf. NOT persisted — a reload always resets the demo.
  // Plan: knowledge/portal/plans/2026-07-16-portal-demo-mode.md
  const demoMode = ref(false)

  // Any change of who is signed in resets every registered per-user store
  // (D1). `sync`, so the reset has run before the caller's next statement: a
  // read B's view starts in the same tick lands on a clean store. Logout is
  // also covered by the explicit call in `clearAuth()`; the watcher is what
  // catches A→B with no teardown between.
  watch(
    () => user.value?.id ?? null,
    (next, prev) => {
      if (next !== prev) resetIdentityStores()
    },
    { flush: 'sync' },
  )

  // ===== Getters =====

  // The effective user for UI purposes (viewingAs takes precedence)
  const effectiveUser = computed(() => viewingAs.value || user.value)

  // True admin status (never affected by viewingAs)
  const isTrueAdmin = computed(() => user.value?.role === 'admin')

  // Whether currently viewing as another user
  const isViewingAs = computed(() => !!viewingAs.value)

  // True when a staff user has a client selected for portal view-as. Drives the
  // read-only treatment (hide action buttons) and the sidebar company name.
  const isViewingAsClient = computed(() => isStaff.value && !!viewAsClientId.value)

  // True when the EFFECTIVE user's own portal_access_level is 'view_only' (the
  // caller's own within-client permission — `users.portal_access_level`,
  // "about themselves", see auth/schemas.py UserRead). This is a UI hint only;
  // the server refuses every unsafe method for view_only regardless
  // (assert_portal_access at current_active_user). A real CLIENT user logged
  // in directly is never staff, so isViewingAsClient is false for them and the
  // old gate let every mutating button render for a view_only client (F4,
  // 2026-08-27) — every POST then 403'd. Staff/admin rows default to 'full'
  // (server_default, applies to ADMIN/STAFF/DEVELOPER too — see
  // 20260812_0002_portal_access_lvl), so this never fires for editOnBehalf/
  // demoMode staff writing on behalf of a viewed client.
  const isOwnAccessViewOnly = computed(
    () => effectiveUser.value?.portal_access_level === 'view_only',
  )

  // True when portal action UI should be interactive: real clients always; staff
  // viewing a client only in edit-on-behalf (LIVE) or demo (simulated) mode —
  // AND, independent of view-as, never for a caller whose own access level is
  // view_only. Views gate their mutating buttons on THIS, not on editOnBehalf
  // directly.
  const portalWriteUiEnabled = computed(
    () =>
      (!isViewingAsClient.value || editOnBehalf.value || demoMode.value) &&
      !isOwnAccessViewOnly.value,
  )

  const isAuthenticated = computed(() => isPortalIdentityMode()
    ? !!portalSession.value && !!user.value
    : mode === 'oidc' ? !!staffSession.value && !!user.value
      : !!token.value && !!user.value)

  // Role checks use effectiveUser so "View As" works
  const isAdmin = computed(() => effectiveUser.value?.role === 'admin')

  const isStaff = computed(() => effectiveUser.value?.role === 'staff' || effectiveUser.value?.role === 'admin')

  const isClient = computed(() => effectiveUser.value?.role === 'client')

  // Restricted developer role — the build surfaces it was granted by name,
  // and Test clients only (see app/auth/client_scope.py). Standalone: NOT implied by
  // isStaff/isAdmin (both false for a developer), so nav/guards gate on this.
  const isDeveloper = computed(() => effectiveUser.value?.role === 'developer')

  // Restricted legal role — the legal department's own surfaces and nothing
  // else. Standalone for the same reason `isDeveloper` is: the backend's
  // is_staff/is_admin are both false for it, so nav and guards gate on this
  // rather than inferring it from isStaff.
  //
  // Deliberately NOT the test for "may see legal content": `CurrentLegal` on
  // the backend admits ADMIN OR LEGAL, so the three operators (all admin) pass
  // it while `isLegal` is false for them. ops/src/router/legal.ts is the one
  // place that pairing is written down; do not re-derive it here.
  const isLegal = computed(() => effectiveUser.value?.role === 'legal')

  const userDisplayName = computed(() => {
    const u = effectiveUser.value
    if (!u) return ''
    if (u.first_name || u.last_name) {
      return [u.first_name, u.last_name].filter(Boolean).join(' ')
    }
    return u.email
  })

  const userInitials = computed(() => {
    const u = effectiveUser.value
    if (!u) return '??'
    if (u.first_name && u.last_name) {
      return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase()
    }
    return u.email.substring(0, 2).toUpperCase()
  })

  const clientName = computed(() => effectiveUser.value?.client_name || null)

  /**
   * API-relative logo path for whichever carrier the portal is currently
   * showing. Two sources, because the two cases know different things:
   *
   * - Staff view-as: `viewAsClientId` IS a client UUID and the public logo
   *   endpoint accepts one, so the URL is derivable with no extra round-trip.
   *   We cannot know from here whether that carrier HAS a logo, so consumers
   *   must handle the 404 (PortalLayout falls back to the ClaimShield mark).
   * - A real client user: `/users/me` answers definitively, and is null for the
   *   three-in-four carriers with no logo — so no wasted request for them.
   */
  const clientLogoUrl = computed(() => {
    if (viewAsClientId.value) return `/api/v1/public/logos/${viewAsClientId.value}`
    return effectiveUser.value?.client_logo_url || null
  })

  // True user display name (never affected by viewingAs)
  const trueUserDisplayName = computed(() => {
    if (!user.value) return ''
    if (user.value.first_name || user.value.last_name) {
      return [user.value.first_name, user.value.last_name].filter(Boolean).join(' ')
    }
    return user.value.email
  })

  // ===== Actions =====

  /**
   * Initialize auth state from stored token.
   * Called on app mount.
   *
   * Embed bootstrap: if the URL carries a `?t=<jwt>` query param (set by the
   * Zoho CRM proposal-dashboard widget), prefer that token over anything in
   * localStorage. This lets the iframe inherit a fresh session without ever
   * prompting Prosser to log in. The token is then written to localStorage so
   * subsequent route navigations inside the iframe don't need to re-pass it.
   */
  // Memoize the in-flight init so the two boot callers — portal App.vue's
  // onMounted and the router beforeEach guard — share ONE /users/me instead of
  // both firing before `initialized` flips in the finally below (P4, 2026-06-17).
  let initializePromise: Promise<void> | null = null

  /** Retry a restore that could not reach the server. */
  async function retryRestore(): Promise<void> {
    restoreUnreachable.value = false
    initialized.value = false
    await initialize()
  }

  async function initialize(): Promise<void> {
    if (initialized.value) return
    if (initializePromise) return initializePromise
    initializePromise = _initialize()
    return initializePromise
  }

  async function _initialize(): Promise<void> {
    loading.value = true

    try {
      if (mode === 'oidc') {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        token.value = null
        refreshToken.value = null
        try {
          staffSession.value = await authService.staffSession()
          clearSignInRedirect()
          user.value = await authService.getCurrentUser()
          beginAuthSession()
          pushAnalyticsIdentity()
        } catch (error) {
          clearAuth()
          // The server said no session (401/403): signed out. Anything else —
          // no answer, or a server error — is "could not connect", same as the
          // password branch below: the login page offers a retry of this restore
          // instead of a sign-in round trip that cannot work either (spec
          // b-app-auth-states surface 2, 2026-10-23).
          if (!isServerRejection(error)) restoreUnreachable.value = true
        }
        return
      }
      if (isPortalIdentityMode()) {
        if (staffViewAsContext.value) return
        // A stale bearer from a previous rollout cannot become a fallback.
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        sessionStorage.removeItem(VIEW_AS_KEY)
        sessionStorage.removeItem(VIEW_AS_CLIENT_KEY)
        token.value = null
        refreshToken.value = null
        try {
          const session = await authService.portalSession()
          setPortalSession(session)
        } catch {
          clearAuth()
        }
        return
      }
      // Embed bootstrap — pull token out of URL if present (CRM widget hand-off)
      let embedToken: string | null = null
      try {
        const params = new URLSearchParams(window.location.search)
        embedToken = params.get('t')
        if (embedToken) {
          localStorage.setItem(TOKEN_KEY, embedToken)
          // Strip the token from the URL so it doesn't sit in the address bar /
          // referer / history. Keep other query params (e.g. `embed=1`).
          params.delete('t')
          const remaining = params.toString()
          const newUrl =
            window.location.pathname +
            (remaining ? `?${remaining}` : '') +
            window.location.hash
          window.history.replaceState({}, '', newUrl)
        }
      } catch (e) {
        console.warn('[Auth] embed-bootstrap failed (non-fatal):', e)
      }

      // Try to restore token from storage (now includes the embed token if any)
      const storedToken = localStorage.getItem(TOKEN_KEY)
      // Restore refresh token too so silent refresh survives a page reload
      refreshToken.value = localStorage.getItem(REFRESH_TOKEN_KEY)

      if (storedToken) {
        // Validate token by fetching current user
        token.value = storedToken

        try {
          const currentUser = await authService.getCurrentUser()
          user.value = currentUser
          // Same reason as in login(): the restored identity starts with no
          // inherited denial (AST3-1).
          beginAuthSession()
          if (import.meta.env.DEV) console.log('[Auth] Session restored for:', currentUser.email)

          // Restore View As if admin
          if (currentUser.role === 'admin') {
            restoreViewingAs()
          }
          // Restore the staff portal view-as-client selection (staff or admin)
          if (currentUser.role === 'staff' || currentUser.role === 'admin') {
            restoreViewAsClient()
          }
          // AFTER the two restores above, deliberately: both can put this
          // session into View As, and identifying before they run would
          // attribute an impersonating session's first events to a carrier.
          pushAnalyticsIdentity()
        } catch (error) {
          if (isServerRejection(error)) {
            // The server answered: the token is invalid or expired (the API
            // client has already tried the refresh token by this point).
            if (import.meta.env.DEV) console.log('[Auth] Stored token invalid, clearing')
            clearAuth()
          } else {
            // The server could not be reached. Keep the stored sign-in; the
            // guard sends the visitor to login with a retry, not a blank form.
            console.warn('[Auth] Session restore could not reach the server; keeping the sign-in', error)
            user.value = null
            restoreUnreachable.value = true
          }
        }
      }
    } finally {
      initialized.value = true
      loading.value = false
      initializePromise = null
    }
  }

  /**
   * Login with email and password
   */
  async function login(credentials: LoginRequest): Promise<void> {
    if (mode === 'oidc') throw new Error('Use staff single sign-on')
    if (isPortalIdentityMode()) throw new Error('Use portal single sign-on')
    loading.value = true

    try {
      // Get token pair from API (access + rotating refresh token)
      const response = await authService.login(credentials)
      token.value = response.access_token
      refreshToken.value = response.refresh_token

      // Persist tokens
      localStorage.setItem(TOKEN_KEY, response.access_token)
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)

      // Fetch user info
      const currentUser = await authService.getCurrentUser()
      user.value = currentUser
      // A new identity owns this document now, so nothing the previous one was
      // denied may be read by it (AST3-1). Not redundant with the clearAuth()
      // above: an account can be replaced without a teardown running first.
      beginAuthSession()
      pushAnalyticsIdentity()

      if (import.meta.env.DEV) console.log('[Auth] Login successful:', currentUser.email)
    } catch (error) {
      clearAuth()
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Update the stored token pair after a silent refresh.
   * Called by the api.ts 401 interceptor once /auth/refresh succeeds.
   */
  function setTokens(accessToken: string, newRefreshToken: string): void {
    if (mode === 'oidc') throw new Error('Bearer refresh is unavailable in staff identity mode')
    if (isPortalIdentityMode()) throw new Error('Bearer refresh is unavailable in portal identity mode')
    token.value = accessToken
    refreshToken.value = newRefreshToken
    localStorage.setItem(TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken)
  }

  /**
   * Set the bearer token IN MEMORY only (no localStorage), for the staff
   * read-only "view-as" dashboard tab. Deliberately not persisted so it never
   * pollutes a real client session in the same browser and expires with the tab.
   */
  function setBearerToken(accessToken: string): void {
    if (isPortalIdentityMode()) staffViewAsContext.value = true
    token.value = accessToken
  }

  function setPortalSession(session: PortalIdentitySession): void {
    if (!isPortalIdentityMode()) throw new Error('Portal session requires identity mode')
    const previousCarrier = portalSession.value?.carrier_id
    const previousPerson = portalSession.value?.person_id
    portalSession.value = session
    clearSignInRedirect()
    // No legacy account profile: the account card's name and email come from
    // the session (the person's own sign-in profile). Keep actor and carrier
    // keys separate; carrier labels are supplied by normal scoped API data.
    const [firstName, ...rest] = (session.display_name || '').trim().split(/\s+/)
    user.value = {
      id: session.person_id, email: session.email || '',
      first_name: firstName || null, last_name: rest.join(' ') || null,
      role: 'client', is_active: true, is_verified: true, is_superuser: false,
      created_at: '', updated_at: null, last_login_at: null,
      zoho_contact_id: null, zoho_account_id: null, client_name: null,
      client_id: session.carrier_id,
      portal_access_level: session.capabilities.includes('billing') && session.capabilities.length === 1
        ? 'billing' : session.capabilities.includes('full') ? 'full'
          : session.capabilities.includes('safety') ? 'safety' : 'view_only',
    }
    if (previousPerson !== session.person_id || previousCarrier !== session.carrier_id) {
      resetIdentityStores()
      beginAuthSession()
    }
    pushAnalyticsIdentity()
  }

  async function selectPortalCarrier(carrierId: string): Promise<void> {
    const session = portalSession.value
    if (!isPortalIdentityMode() || !session) throw new Error('Portal session unavailable')
    if (!session.authorized_carrier_ids.includes(carrierId)) throw new Error('Carrier is not authorized')
    await authService.selectPortalCarrier(carrierId, session.csrf_token)
    setPortalSession(await authService.portalSession())
  }

  /**
   * Logout and clear all auth state.
   *
   * `reason: 'denied'` is the guard ending a session for an account no route in this app
   * admits: the sign-in server's sign-out then hands `state=denied` back and the app shows
   * "This account doesn't have access" rather than "You're signed out", with the refused
   * account no longer remembered, so "Use a different account" opens a clean email page.
   * On that path a failed server call still clears the local state (the guard has already
   * decided), and the portal follows the sign-in server's sign-out too.
   */
  async function logout(options: { reason?: LogoutReason } = {}): Promise<void | string | null> {
    const { reason } = options
    if (mode === 'oidc') {
      const session = staffSession.value
      if (!session) { clearAuth(); return }
      markSigningOut()
      let logoutUrl: string | null
      try {
        logoutUrl = await (reason ? authService.staffLogout(session.csrf_token, reason)
          : authService.staffLogout(session.csrf_token))
      } catch (err) {
        if (!reason) throw err
        clearAuth()
        return null
      }
      clearAuth()
      if (logoutUrl) window.location.assign(logoutUrl)
      return logoutUrl
    }
    if (isPortalIdentityMode() && !staffViewAsContext.value) {
      const session = portalSession.value
      if (!session) { clearAuth(); return }
      markSigningOut()
      let logoutUrl: string | null
      try {
        logoutUrl = await (reason ? authService.portalLogout(session.csrf_token, reason)
          : authService.portalLogout(session.csrf_token))
      } catch (err) {
        if (!reason) throw err
        clearAuth()
        return null
      }
      clearAuth()
      if (reason && typeof logoutUrl === 'string' && logoutUrl.startsWith('https://')) {
        window.location.assign(logoutUrl)
      }
      return logoutUrl
    }
    try {
      // Optional: notify backend of logout
      // await authService.logout()
    } finally {
      clearAuth()
      if (import.meta.env.DEV) console.log('[Auth] Logged out')
    }
  }

  /**
   * Tell analytics who this session belongs to.
   *
   * Reads the store's own state rather than taking arguments, so every caller
   * agrees on the answer and none of them can pass a stale one. A no-op unless
   * the app installed the PostHog plugin and the host is on its allowlist.
   *
   * `user.value`, deliberately — NOT `effectiveUser`. While an admin is using
   * View As, `effectiveUser` is the person being viewed, and identifying as
   * them would file an admin's support session under a carrier's own activity.
   * The real user is identified instead, the carrier group is withheld, and the
   * session is stamped `impersonating` so it can be filtered out. See
   * `syncIdentity` in plugins/posthog.ts for why that matters more than it
   * looks.
   */
  function pushAnalyticsIdentity(): void {
    const realUser = user.value
    if (!realUser) {
      syncIdentity(null)
      return
    }
    const impersonating = viewingAs.value !== null || viewAsClientId.value !== null
    syncIdentity({
      userId: realUser.id,
      clientId: realUser.client_id ?? null,
      impersonating,
    })
  }

  /**
   * Clear auth state and storage
   */
  function clearAuth(): void {
    // Before the state is torn down, so the next person on this browser is a
    // new person rather than an extension of this one. Covers logout and the
    // invalid-token path in _initialize alike.
    syncIdentity(null)
    // The denied URL of whoever was just signed out (AST3-1, 2026-09-22). It is
    // cleared HERE rather than in `logout()` because this is the one teardown
    // both logout and a failed session restore go through — a fix hung off
    // `logout()` alone would leave the carrier behind on the expired-token path,
    // which is the commoner of the two.
    clearRouteDenial()
    user.value = null
    token.value = null
    refreshToken.value = null
    portalSession.value = null
    staffSession.value = null
    staffViewAsContext.value = false
    viewingAs.value = null
    viewAsClientId.value = null
    viewAsClientName.value = null
    editOnBehalf.value = false
    demoMode.value = false
    resetDemoOverlay()
    // No per-user store survives a logout on a shared machine (S22-C2, D1).
    // Registered rather than imported: this package cannot reach into an ops
    // store without inverting the dependency, so each store calls
    // registerIdentityReset() in its own setup(). Called here as well as from
    // the user.id watcher so a teardown with no signed-in user (a failed
    // restore) still resets.
    resetIdentityStores()
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    sessionStorage.removeItem(VIEW_AS_KEY)
    sessionStorage.removeItem(VIEW_AS_CLIENT_KEY)
  }

  /**
   * Check if user has a specific role (uses effectiveUser for View As)
   */
  function hasRole(role: UserRole): boolean {
    const u = effectiveUser.value
    if (!u) return false

    // Admin has access to everything
    if (u.role === 'admin') return true

    // Staff has access to staff and client
    if (u.role === 'staff' && (role === 'staff' || role === 'client')) return true

    // Client only has client role
    return u.role === role
  }

  /**
   * Set viewing as another user (admin only)
   */
  function setViewingAs(targetUser: User): void {
    if (!isTrueAdmin.value) {
      console.warn('[Auth] Only admins can use View As feature')
      return
    }
    viewingAs.value = targetUser
    sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(targetUser))
    pushAnalyticsIdentity()
    if (import.meta.env.DEV) console.log('[Auth] Now viewing as:', targetUser.email)
  }

  /**
   * Clear viewing as and return to own view
   */
  function clearViewingAs(): void {
    viewingAs.value = null
    sessionStorage.removeItem(VIEW_AS_KEY)
    pushAnalyticsIdentity()
    if (import.meta.env.DEV) console.log('[Auth] Cleared View As, back to own view')
  }

  /**
   * Restore viewingAs from session storage (called during initialize)
   */
  function restoreViewingAs(): void {
    if (!isTrueAdmin.value) return

    const stored = sessionStorage.getItem(VIEW_AS_KEY)
    if (stored) {
      try {
        viewingAs.value = JSON.parse(stored)
        if (import.meta.env.DEV) console.log('[Auth] Restored View As:', viewingAs.value?.email)
      } catch {
        sessionStorage.removeItem(VIEW_AS_KEY)
      }
    }
  }

  /**
   * Portal staff "view-as client": scope every portal tab to a chosen client
   * (read-only). Pass (null, null) to exit. Persisted to sessionStorage so it
   * survives tab navigation; the api request interceptor reads viewAsClientId
   * to append ?client_id= on portal calls.
   */
  function setViewAsClient(id: string | null, name: string | null): void {
    viewAsClientId.value = id
    viewAsClientName.value = name
    editOnBehalf.value = false // switching/clearing the client always drops edit mode
    demoMode.value = false // switching/clearing the client always exits demo
    resetDemoOverlay()
    if (id) {
      sessionStorage.setItem(VIEW_AS_CLIENT_KEY, JSON.stringify({ id, name }))
    } else {
      sessionStorage.removeItem(VIEW_AS_CLIENT_KEY)
    }
    // Entering OR leaving a client scope changes whether this session is an
    // impersonation, so analytics has to be told both times.
    pushAnalyticsIdentity()
  }

  /**
   * Toggle staff/admin "edit as this client". Only effective while viewing a
   * client; never persisted (opt-in per session). Gates the X-Edit-On-Behalf
   * header. Spec: knowledge/portal/specs/2026-06-22-edit-on-behalf-design.md
   */
  function setEditOnBehalf(on: boolean): void {
    editOnBehalf.value = on && isViewingAsClient.value
    if (editOnBehalf.value && demoMode.value) {
      demoMode.value = false
      resetDemoOverlay()
    }
  }

  /**
   * Toggle staff/admin demo mode on the viewed client. Registered mutations get
   * simulated client-side; the overlay resets on every transition so each demo
   * starts clean. Mutually exclusive with edit-on-behalf (LIVE writes).
   */
  function setDemoMode(on: boolean): void {
    demoMode.value = on && isViewingAsClient.value
    if (demoMode.value) editOnBehalf.value = false
    resetDemoOverlay()
  }

  /** Restore the staff view-as-client selection from session storage (staff/admin). */
  function restoreViewAsClient(): void {
    if (!isStaff.value) return
    const stored = sessionStorage.getItem(VIEW_AS_CLIENT_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        viewAsClientId.value = parsed.id ?? null
        viewAsClientName.value = parsed.name ?? null
      } catch {
        sessionStorage.removeItem(VIEW_AS_CLIENT_KEY)
      }
    }
  }

  /**
   * Check if user has any of the specified roles
   */
  function hasAnyRole(roles: UserRole[]): boolean {
    return roles.some((role) => hasRole(role))
  }

  /**
   * Update user profile locally (after API update)
   */
  function updateUserProfile(updates: Partial<User>): void {
    if (user.value) {
      user.value = { ...user.value, ...updates }
    }
  }

  return {
    // State
    mode,
    user,
    token,
    refreshToken,
    portalSession,
    staffSession,
    staffViewAsContext,
    initialized,
    restoreUnreachable,
    retryRestore,
    loading,
    viewingAs,
    viewAsClientId,
    viewAsClientName,
    editOnBehalf,
    setEditOnBehalf,
    demoMode,
    setDemoMode,

    // Getters
    isAuthenticated,
    isAdmin,
    isStaff,
    isClient,
    isDeveloper,
    isLegal,
    isTrueAdmin,
    isViewingAs,
    isViewingAsClient,
    portalWriteUiEnabled,
    effectiveUser,
    userDisplayName,
    userInitials,
    clientName,
    clientLogoUrl,
    trueUserDisplayName,

    // Actions
    initialize,
    login,
    setTokens,
    setBearerToken,
    setPortalSession,
    selectPortalCarrier,
    logout,
    clearAuth,
    hasRole,
    hasAnyRole,
    updateUserProfile,
    setViewingAs,
    clearViewingAs,
    setViewAsClient,
  }
})
